import { useState } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';

const token = /\{\{\s*([A-Za-z_][\w.]*)\s*\}\}/g;
export const promptVariables = (text: string) => [
  ...new Set([...text.matchAll(token)].map((match) => match[1])),
];
export const previewPrompt = (text: string, values: Record<string, string>) =>
  text.replace(token, (raw, name: string) =>
    Object.hasOwn(values, name) && values[name] !== '' ? values[name] : raw,
  );

export const StudioPromptVariables = ({
  prompt,
  greeting,
  agentName,
  agentId,
  campaignId,
  readOnly,
  onInsert,
}: {
  prompt: string;
  greeting: string;
  agentName: string;
  agentId: string;
  campaignId: string;
  readOnly: boolean;
  onInsert: (name: string) => void;
}) => {
  const [values, setValues] = useState<Record<string, string>>({});
  const [name, setName] = useState('');
  const variables = promptVariables(`${prompt}\n${greeting}`);
  return (
    <Stack gap={2}>
      <Alert severity="info">
        Local text preview only. Test values are kept in this dialog, never saved or sent to a
        provider. Empty values leave placeholders unresolved. This does not validate runtime
        template support or execute tools.
      </Alert>
      {variables.map((variable) => (
        <TextField
          key={variable}
          label={`Test value: ${variable}`}
          value={values[variable] ?? ''}
          onChange={(event) => setValues({ ...values, [variable]: event.target.value })}
          slotProps={{ htmlInput: { maxLength: 4000 } }}
        />
      ))}
      {!variables.length && (
        <Typography>No template variables found in these instructions or greeting.</Typography>
      )}
      <Typography variant="subtitle1">Current editor context</Typography>
      <Typography>Agent name: {agentName}</Typography>
      <Typography>Agent ID: {agentId}</Typography>
      <Typography>Campaign ID: {campaignId}</Typography>
      <Typography variant="body2" color="text.secondary">
        These are editor facts, not automatic template substitutions. Call ID, caller ID and
        customer phone are unavailable in this draft. No customer record is fetched.
      </Typography>
      <Typography variant="subtitle1">API results</Typography>
      <Typography variant="body2">
        The current native tool contract exposes no response-variable extraction mappings. No API
        result is inferred or fetched for this preview.
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
        <TextField
          label="New variable"
          value={name}
          disabled={readOnly}
          onChange={(event) => setName(event.target.value)}
          slotProps={{ htmlInput: { maxLength: 120 } }}
          helperText="Letters, numbers and underscores; start with a letter or underscore."
        />
        <Button
          disabled={readOnly || !/^[A-Za-z_][A-Za-z0-9_]*$/.test(name)}
          onClick={() => {
            if (readOnly || !/^[A-Za-z_][A-Za-z0-9_]*$/.test(name)) return;
            onInsert(name);
            setName('');
          }}>
          Insert in prompt
        </Button>
      </Stack>
      <Typography variant="subtitle1">Instructions preview</Typography>
      <Box
        component="pre"
        aria-label="Instructions preview"
        sx={{ m: 0, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
        {previewPrompt(prompt, values)}
      </Box>
      <Typography variant="subtitle1">Greeting preview</Typography>
      <Box
        component="pre"
        aria-label="Greeting preview"
        sx={{ m: 0, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
        {previewPrompt(greeting, values) || 'No greeting configured.'}
      </Box>
    </Stack>
  );
};
